import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import message from 'antd/es/message';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpPage } from 'new-components/OpPage/OpPage';
import { queryRequest } from 'utils/queryRequest';
import { OpInfo } from 'new-components/DLS/OpInfo/OpInfo';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';

import { useValidateAndSetToken } from './helpers/useValidateAndSetToken';
import { useGuestAccessLinkLabel } from './helpers/useGuestAccessLinkLabel';

import './CloudKeyUnlockPage.scss';

const CloudKeyUnlockPage = () => {
  const { t } = useTranslation();
  const guestAccessLinkLabel = useGuestAccessLinkLabel();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [status, setStatus] = useState('');
  const [unlockEntryId, setUnlockEntryId] = useState<number>();

  const {
    isLoading,
    errorMessage,
    errorDescription,
    token,
    decodedJwt: {
      exp: tokenExpirationDate,
      entryId: tokenEntryId,
      entryData: tokenEntryData,
    },
  } = useValidateAndSetToken({
    messageApi,
  });

  const cloudKeyUnlock = async (entryIdToUnlock: number) => {
    setStatus('processing');
    setUnlockEntryId(entryIdToUnlock);

    const currentIdentityLanguage = localStorage.getItem('i18nextLng') || 'en';

    const useCloudKeyUnlockTokenResponse = await queryRequest({
      apiEndpointName: 'useCloudKeyUnlockToken',
      parameters: [token, { entryId: entryIdToUnlock }],
      currentIdentityLanguage,
    });

    if (useCloudKeyUnlockTokenResponse.statusCode >= 400) {
      messageApi.error(useCloudKeyUnlockTokenResponse.json.message);
      setStatus('fail');
    } else {
      setStatus('success');
      messageApi.success(t('Unlock request successfully sent'));
    }
  };

  const renderStatus = () => {
    switch (status) {
      case 'processing':
        return (
          <LoadingOutlined className="cloud-key-unlock-page__loading-icon" />
        );
      case 'success':
        return (
          <CheckCircleFilled className="cloud-key-unlock-page__success-icon" />
        );
      case 'fail':
        return (
          <CloseCircleFilled className="cloud-key-unlock-page__error-icon" />
        );

      default:
        return null;
    }
  };

  const createContent = () => {
    if (isLoading) {
      return (
        <div className="cloud-key-unlock-page__loader">
          <LoadingOutlined />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <OpInfo
          closable // Need to be able to get the space to see the buttons in the case of font scaling
          type="error"
          message={errorMessage}
          description={errorDescription}
        />
      );
    }

    return (
      <>
        <div>
          <OpInfo
            closable // Need to be able to get the space to see the buttons in the case of font scaling
            description={
              <OpSpace direction="vertical">
                <div>
                  {t(
                    "Hello! You've arrived at this page because someone has shared an {{guestAccessLinkLabel}} with you, allowing you to unlock an entry by clicking the button(s) below.",
                    { guestAccessLinkLabel },
                  )}
                </div>
                {tokenExpirationDate && (
                  <div>
                    {t('This access will expire at {{date}}.', {
                      date: new Date(
                        tokenExpirationDate * 1000,
                      ).toLocaleString(),
                    })}
                  </div>
                )}
                <div>
                  {t(
                    'Please note that all unlock activity will be logged and visible to the administrators of the Avigilon Alta account that owns the entry.',
                  )}
                </div>
              </OpSpace>
            }
          />
        </div>

        <h3 className="cloud-key-unlock-page__entry-list-label">
          {t('Tap to Unlock an Entry:')}
        </h3>

        {typeof tokenEntryId === 'number' ? (
          <div className="cloud-key-unlock-page__entry">
            <div style={{ minWidth: 'fit-content' }}>
              <OpButton
                type="primary"
                onClick={() => {
                  cloudKeyUnlock(tokenEntryId!);
                }}
              >
                {t('Click to unlock')}
              </OpButton>
            </div>
            {renderStatus()}
          </div>
        ) : (
          <div className="cloud-key-unlock-page__entry-list">
            {(tokenEntryData || []).map(({ entryId, uiLabel }) => (
              <div key={entryId} className="cloud-key-unlock-page__entry">
                <div style={{ minWidth: 'fit-content' }}>
                  <OpButton
                    key={entryId}
                    type="primary"
                    onClick={() => cloudKeyUnlock(entryId)}
                  >
                    {uiLabel}
                  </OpButton>
                </div>
                {unlockEntryId === entryId && renderStatus()}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <OpPage className="cloud-key-unlock-page" title={t('Guest access')}>
      {/** For messages to be displayed */}
      {messageContextHolder}

      {createContent()}
    </OpPage>
  );
};

export default CloudKeyUnlockPage;
